import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from "@angular/router";
import { forkJoin, Observable, of, Subject } from "rxjs";
import { catchError, finalize, switchMap, takeUntil, tap } from "rxjs/operators";

// Services / Subjects
import { FeatureService } from "../../../services/feature.service";
import { ValueService } from "../../../services/value.service";
import { PropertyService } from "../../../services/property.service";
import { PropertyInitialSubject } from "../../../subject/property.initial.subject";
import { FlowModeSubject } from "../../../subject/flow.mode.subject";
import { FlowService } from "../../../services/flow.service";
import { FlowStateService } from "../../../services/flow.state.service";

// DTOs / Enums
import { GroupResponseDto } from "../../../dto/group.response.dto";
import { PropertyInitialResponseDto } from "../../../dto/property.initial.response.dto";
import { FlowMode } from "../../../dto/enum/flow.mode.enum";
import { RecordStageEnum } from "../../../dto/enum/record.stage.enum";

const STEP_KEY = 'details-secondary';

@Component({
    selector: 'app-property-details-secondary',
    templateUrl: './property-details-secondary.component.html',
    styleUrls: ['./property-details-secondary.component.scss']
})
export class PropertyDetailsSecondaryComponent implements OnInit, OnDestroy {
    // --------- Flujo / navegación -------------
    currentMode: FlowMode = FlowMode.CREATE;
    previousUrl = '';
    nextUrl = '';

    // --------- Propiedad / backend -------------
    propertyInitial: PropertyInitialResponseDto | null = null;
    propertyId: number | undefined;
    segmentLabel: string | undefined = '';

    // --------- UI / estado -------------
    loading = true;
    showErrorDialog = false;
    dialogCode: string = 'ERROR';
    dialogMessage: string = 'Debes seleccionar al menos una característica';
    private destroy$ = new Subject<void>();

    // --------- Datos del paso -------------
    featureGroups: GroupResponseDto[] = [];
    selectedFeatures: { [featureCode: string]: boolean } = {}; // selección booleana por feature
    otherValues: Record<string, string> = {};
    private valueIdByFeatureCode: { [featureCode: string]: number } = {};

    constructor(
        private router: Router,
        private featureService: FeatureService,
        private valueService: ValueService,
        private propertyService: PropertyService,
        private propertyInitialSubject: PropertyInitialSubject,
        private flowModeSubject: FlowModeSubject,
        private flowService: FlowService,
        private flowState: FlowStateService
    ) {}

    // -----------------------------------------------------------
    // Ciclo de vida
    // -----------------------------------------------------------
    ngOnInit(): void {
        // 1) Modo y rutas
        const mode = this.flowModeSubject.getCurrentMode();
        if (!mode) { this.router.navigate(['/list-pending']); return; }
        this.currentMode = mode;
        this.previousUrl = this.flowService.getPreviousStep(STEP_KEY);
        this.nextUrl = this.flowService.getNextStep(STEP_KEY);

        // 2) Paso actual
        this.flowState.setCurrentStep(STEP_KEY);

        // 3) Propiedad y stage
        this.propertyInitial = this.propertyInitialSubject.getCurrentProperty();
        if (!this.propertyInitial?.propertyId) {
            this.router.navigate(['/list-pending']);
            return;
        }
        this.propertyId = this.propertyInitial.propertyId;
        this.segmentLabel = this.propertyInitial.segmentLabel || 'propiedad';
        if (this.propertyInitial.recordStage) {
            this.flowState.setRecordStage(this.propertyInitial.recordStage as RecordStageEnum);
        }
        this.flowState.setPropertyId(this.propertyId);

        // 4) Cargar features + aplicar cache
        this.loadFeatureSecondary();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    // -----------------------------------------------------------
    // Carga de características secundarias
    // -----------------------------------------------------------
    private loadFeatureSecondary(): void {
        if (!this.propertyId) return;
        this.loading = true;

        this.featureService.getValueSecondaryListByPropertyId(this.propertyId).pipe(
            tap((response: GroupResponseDto[]) => {
                this.featureGroups = response || [];
                this.initializeSelectionFromBackend(); // opcional: si el backend trae valores preseleccionados
                this.applyCache(); // sobrescribe con cache local
            }),
            finalize(() => this.loading = false),
            catchError((error) => {
                console.error('Error fetching secondary features:', error);
                this.showErrorDialog = true;
                this.dialogMessage = 'No se pudieron cargar las características.';
                return of([]);
            }),
            takeUntil(this.destroy$)
        ).subscribe();
    }

    // Si backend trae algo como value === '1' / true, márcalo seleccionado
    private initializeSelectionFromBackend(): void {
        const map: { [code: string]: boolean } = {};
        this.valueIdByFeatureCode = {};
        this.featureGroups.forEach(group => {
            group.featureList.forEach(f => {
                if (f.valueId) {
                    this.valueIdByFeatureCode[f.featureCode] = f.valueId;
                }
                const isSelected = !!f.valueId || f.mandatory === 1;
                map[f.featureCode] = isSelected;
            });
        });
        this.selectedFeatures = map;
        this.persistToFlowState();
    }


    // -----------------------------------------------------------
    // Cache local (FlowState)
    // -----------------------------------------------------------
    private persistToFlowState(): void {
        this.flowState.setStepData(STEP_KEY, {
            selectedFeatures: { ...this.selectedFeatures }
        });
    }

    private applyCache(): void {
        const cached = this.flowState.getStepData(STEP_KEY);
        if (!cached?.selectedFeatures) return;

        // Reconciliar: solo para features que existen actualmente
        Object.keys(cached.selectedFeatures).forEach(code => {
            const exists = this.featureGroups.some(g => g.featureList.some(f => f.featureCode === code));
            if (exists) this.selectedFeatures[code] = !!cached.selectedFeatures[code];
        });
    }

    // -----------------------------------------------------------
    // UI
    // -----------------------------------------------------------
    toggleFeatureSelection(featureCode: string): void {
        this.selectedFeatures[featureCode] = !this.selectedFeatures[featureCode];
        this.persistToFlowState();
    }

    // -----------------------------------------------------------
    // Guardado y navegación
    // -----------------------------------------------------------
    private buildPayload(): any[] {
        // Sólo enviar las seleccionadas
        return Object.keys(this.selectedFeatures)
            .filter(code => this.selectedFeatures[code])
            .map(code => ({ featureCode: code, value: this.otherValues[code] || '1' }));
    }

    private saveFeatureValues(): Observable<any> {
        if (!this.propertyId) return of(null);

        const payload = this.buildPayload();
        const newValues = payload.filter(item => !this.valueIdByFeatureCode[item.featureCode]);
        const updateRequests = payload
            .filter(item => !!this.valueIdByFeatureCode[item.featureCode])
            .map(item => this.valueService.updateFeatureValue(this.valueIdByFeatureCode[item.featureCode], item.value));

        const requests: Observable<any>[] = [
            ...(newValues.length ? [this.valueService.registerFeatureValues(this.propertyId, newValues)] : []),
            ...updateRequests
        ];

        return requests.length ? forkJoin(requests) : of(null);
    }

    private isValidSelection(): boolean {
        return Object.values(this.selectedFeatures).some(v => !!v);
    }

    private shouldAdvanceStage(): boolean {
        if (this.currentMode === FlowMode.EDIT) return false;
        if (this.currentMode === FlowMode.CREATE) return true;

        const stage = this.flowState.recordStage;
        if (!stage) return false;

        const stageStep = this.flowState.getStepByRecordStage(stage);
        const currentIdx = this.flowService.getStepIndex(STEP_KEY);
        const stageIdx = this.flowService.getStepIndex(stageStep);

        if (currentIdx === -1 || stageIdx === -1) return false;
        return currentIdx > stageIdx; // posterior al stage → avanza
    }

    handleNextStep(): void {
        // Validación: al menos una seleccionada
        if (!this.isValidSelection()) {
            this.showErrorDialog = true;
            this.dialogCode = 'VALIDATION';
            this.dialogMessage = 'Debes seleccionar al menos una característica';
            return;
        }

        this.persistToFlowState();

        this.saveFeatureValues().pipe(
            tap(() => {
                // deja constancia local del stage
                this.flowState.setRecordStage(RecordStageEnum.FEATURES_2);
            }),
            switchMap(() => {
                if (this.shouldAdvanceStage()) {
                    const body = { id: this.propertyId };
                    return this.propertyService.nextStageProperty(body).pipe(
                        catchError((err) => { console.error('nextStage error', err); return of(null); })
                    );
                }
                return of(null);
            }),
            takeUntil(this.destroy$)
        ).subscribe(() => {
            this.router.navigate([this.nextUrl]); // '/pictures' u otro según tu FlowService
        });
    }

    handleSaveAndExit(): void {
        // Guardar pero NO avanzar
        if (!this.isValidSelection()) {
            this.showErrorDialog = true;
            this.dialogCode = 'VALIDATION';
            this.dialogMessage = 'Debes seleccionar al menos una característica';
            return;
        }

        this.persistToFlowState();

        this.saveFeatureValues().pipe(
            catchError((err) => { console.error('save error', err); return of(null); }),
            takeUntil(this.destroy$)
        ).subscribe(() => {
            this.router.navigate(['/list-pending']); // o dashboard
        });
    }

    handlePreviousStep(): void {
        this.persistToFlowState();
        this.router.navigate([this.previousUrl]);
    }

    // -----------------------------------------------------------
    // trackBy helpers
    // -----------------------------------------------------------
    trackByGroup = (_: number, g: any) => g?.groupId ?? g?.groupLabel;
    trackByFeature = (_: number, f: any) => f?.featureCode;


    isSelected(code: string): boolean {
        return !!this.selectedFeatures[code];
    }


    selectedCount(): number {
        return Object.values(this.selectedFeatures).filter(Boolean).length;
    }

}
